// ØVINGSMANUSET FOR ULL — OPPDIKTET. Aldri på uten at jegeren har slått det på
// selv, og aldri i det hele tatt på et lag med en konfigurert bro.
//
// ══ HVA SOM ER HER, OG HVA SOM BOR I PAKKEN ════════════════════════════════
// Mekanikken — flagget som følger dataen, porten som nekter øving når det finnes
// en bro, vakta på den utgående EMCON-køen, kilden som kjører manuset — ligger i
// `@skade/sensorflate/ovelse.ts`, delt med NJORD. Les toppkommentaren der først;
// de fire ufravikelige kravene står i sin helhet bare der.
//
// Denne fila er MANUSET: hva en ubevoktet bakkesensor faktisk måler, og hvilke
// tilfeller sekvensanalysen MÅ håndtere riktig. Det kan bare ULL vite.
//
// ══ TALLENE ER VALGT FOR Å PRØVE ANALYSEN, IKKE FOR Å SE PENE UT ═══════════
// Hvert av de fem tilfellene under finnes fordi det er et tilfelle UI-et MÅ
// håndtere riktig, og som er lett å bomme på:
//
//   A. En REN sekvens der rekkefølgen ER bestemt og farten blir et INTERVALL
//      (0,8–2,0 m/s — gange). Positivkontrollen.
//   B. To sensorer 11 m fra hverandre, 2 s mellom, med ±4 s klokkeslark på den
//      ene. Da er REKKEFØLGEN IKKE BESTEMT, og skjermen skal si det og IKKE
//      tegne en pil. Dette er feilen som er lettest å ikke oppdage.
//   C. 520 m på 20 s = 26 m/s. Selv NEDRE fartsgrense er umulig til fots ⇒
//      minst TO ulike mål, eller en klokke som er feil.
//   D. Én ENSLIG kamerautløsning. Kontrakten NEKTER å analysere den, og teksten
//      den nekter med er hele premisset for appen: ett punkt bærer ingen retning.
//   E. Et bilde et menneske HAR sett (dyr), ved siden av to som ingen har åpnet.
//
// Sensorene er dessuten satt opp så dekningsanalysen har noe å si: én hentet
// node (dekker ingenting), én med tapt lenke (teller som dekning helt til man
// krever varsling), og ett magnetometer som ikke dekker en meter av aksen.
//
// ══ ØVINGSDATA SKAL VÆRE REALISTISK UFULLSTENDIG ═══════════════════════════
// Ingenting her er pent. Posisjonen på magnetometeret er ANSLÅTT, klokka på den
// seismiske er fri RTC (som er nettopp derfor fartsintervallet blir bredt), to
// bilder er uvurdert, og en node har vært stille i timer. En jeger som øver på
// perfekt data lærer å stole på tall som ikke fortjener det, og det er den ene
// feilen denne appen er skrevet for å hindre.
//
// ══ HVORFOR EN PULS, OG IKKE BARE ETT ØYEBLIKKSBILDE ═══════════════════════
// Den forrige demokilden sendte alt ÉN gang. En UGS-flate skiller LIVSTEGN
// (hjertebank) fra SISTE UTLØSNING, og uten en puls kan man ikke øve på skillet i
// det hele tatt: begge aldrene vokser monotont, og etter noen minutter ser hver
// node død ut. Pulsen sender derfor `status`-meldinger (livstegn og synkende
// batteri) mye oftere enn utløsninger — som er nøyaktig slik en UGS-mesh
// oppfører seg, og som er hele grunnen til at stolpen har to alderskolonner.
// Se `ovelsepuls` nederst for hvilke hull den bevisst IKKE fyller.
import { project, type LatLon, type UgsSensor, type UgsUtlosning } from '@skade/contracts';
import type { Rute, Ulllag, UllMelding } from './typer.js';

/**
 * Aksen øvingsdataen ligger langs: en skogsvei i indre Oslofjord-området.
 *
 * Posisjonene regnes med kontraktens egen `project()` framfor å skrives som
 * lat/lon-litteraler. To grunner: avstandene blir EKSAKTE (80 m er 80 m, ikke
 * «omtrent»), og de blir lesbare — «S2 står 210 m langs aksen» er noe man kan
 * etterprøve mot fartsintervallene i kommentarene over.
 */
const AKSE_START: LatLon = { lat: 59.55, lon: 10.72 };
const AKSE_KURS = 70;

/** Punkt `langsM` meter langs aksen, `sideM` meter til høyre for den. */
function pkt(langsM: number, sideM = 0): LatLon {
  const påAksen = project(AKSE_START, AKSE_KURS, langsM);
  return sideM === 0 ? påAksen : project(påAksen, AKSE_KURS + 90, sideM);
}

const OVELSE_LAG: Ulllag = {
  id: 'ØVING-LAG-3',
  navn: 'ØVING — Jegerlag 3',
  kallesignal: 'ØVING VARG 3',
  distrikt: 'HV-01',
  omrade: 'Follo',
};

/**
 * Sensorene. `radiusM` er bevisst LAVERE enn `TYPISK_DEKNING_M_UVERIFISERT` i
 * kontrakten, fordi tabellen der er databladtall i fri sikt: tett gran halverer
 * akustisk rekkevidde og stopper PIR helt. Her later vi som laget har MÅLT dem i
 * dette terrenget, som er nettopp det kontrakten ber om.
 */
function ovelseSensorer(naaMs: number): UgsSensor[] {
  const utlagt = naaMs - 172_800_000; // to døgn siden — batteritallene er tilsvarende gamle
  const felles = { lagId: OVELSE_LAG.id, utlagtMs: utlagt, utlagtAv: 'ØVING VARG 3' } as const;
  return [
    {
      ...felles,
      sensorId: 'ovelse-s1-akustisk',
      navn: 'ØVING UGS-1 Akustisk vestre',
      art: 'akustisk',
      ...pkt(0),
      posisjonMetode: 'gps',
      geometri: { form: 'rundstrålende', radiusM: 45 },
      batteri: { enhet: 'volt', spenningV: 7.6, celler: 2, kilde: 'målt' },
      lenke: { kvalitet: 'god', sistHortMs: naaMs - 240_000, rssiDbm: -84, snrDb: 9, hopp: 1 },
      skjultGrad: 'godt-skjult',
      aktiv: true,
      klokkeKilde: 'nettverkssynk',
    },
    {
      ...felles,
      sensorId: 'ovelse-s2-seismisk',
      navn: 'ØVING UGS-2 Seismisk sti',
      art: 'seismisk',
      ...pkt(210),
      posisjonMetode: 'kartlagt',
      geometri: { form: 'rundstrålende', radiusM: 18 },
      batteri: { enhet: 'prosent', prosent: 62, kilde: 'anslått' },
      lenke: { kvalitet: 'god', sistHortMs: naaMs - 300_000, hopp: 2 },
      skjultGrad: 'godt-skjult',
      aktiv: true,
      // Fri RTC: dette er grunnen til at fartsintervallene under blir brede.
      klokkeKilde: 'intern-rtc',
      merknad: 'Ligger i fuktig myrkant — utslagene er dempet, radien er målt til 18 m med egen gange',
    },
    {
      ...felles,
      sensorId: 'ovelse-s3-pir',
      navn: 'ØVING UGS-3 PIR dobbelstråle',
      art: 'pir',
      ...pkt(390),
      posisjonMetode: 'gps',
      // Sektor, ikke rundstrålende: en dobbelstråle-PIR SKAL kunne melde
      // passeringsretning, og kontrakten avviser retning fra en rundstrålende
      // sensor (`validerUtlosningMotSensor`). Strålene peker langs stien.
      geometri: { form: 'sektor', radiusM: 12, retningDeg: 70, apningsvinkelDeg: 40, retningUsikkerhetDeg: 8 },
      batteri: { enhet: 'volt', spenningV: 3.9, celler: 1, kilde: 'målt' },
      lenke: { kvalitet: 'god', sistHortMs: naaMs - 60_000, hopp: 1 },
      skjultGrad: 'delvis-skjult',
      aktiv: true,
      klokkeKilde: 'gps',
    },
    {
      ...felles,
      sensorId: 'ovelse-s4-kamera',
      navn: 'ØVING UGS-4 Viltkamera',
      art: 'kamera',
      ...pkt(470),
      posisjonMetode: 'gps',
      geometri: { form: 'sektor', radiusM: 15, retningDeg: 250, apningsvinkelDeg: 50, retningUsikkerhetDeg: 10 },
      batteri: { enhet: 'prosent', prosent: 14, kilde: 'målt' },
      lenke: { kvalitet: 'marginal', sistHortMs: naaMs - 900_000, rssiDbm: -108, snrDb: 2, hopp: 3 },
      // IR-blitsen lyser rødt i mørket for alle som ser mot den.
      skjultGrad: 'delvis-skjult',
      eksponeringsrisiko: 'høy',
      aktiv: true,
      klokkeKilde: 'nettverkssynk',
    },
    {
      ...felles,
      sensorId: 'ovelse-s5-trad',
      navn: 'ØVING UGS-5 Brytertråd over sti',
      art: 'brytertråd',
      ...pkt(520),
      posisjonMetode: 'gps',
      // Tråden strekkes PÅ TVERS av aksen (70° + 90°), så den som følger stien
      // må krysse den.
      geometri: { form: 'linje', lengdeM: 9, retningDeg: 160 },
      batteri: { enhet: 'volt', spenningV: 3.7, celler: 1, kilde: 'målt' },
      lenke: { kvalitet: 'god', sistHortMs: naaMs - 120_000, hopp: 1 },
      skjultGrad: 'delvis-skjult',
      aktiv: true,
      klokkeKilde: 'gps',
    },
    {
      ...felles,
      sensorId: 'ovelse-s6-magnetisk',
      navn: 'ØVING UGS-6 Magnetometer',
      art: 'magnetisk',
      // 10 m til siden for aksen, med 4 m rekkevidde: den dekker IKKE en eneste
      // meter av ruta, og dekningsanalysen skal vise det.
      ...pkt(400, 10),
      posisjonMetode: 'anslått',
      geometri: { form: 'rundstrålende', radiusM: 4 },
      batteri: { enhet: 'volt', spenningV: 3.6, kilde: 'anslått' },
      lenke: { kvalitet: 'god', sistHortMs: naaMs - 480_000, hopp: 2 },
      skjultGrad: 'godt-skjult',
      aktiv: true,
      klokkeKilde: 'ukjent',
      merknad: 'Lagt ut i mørket etter kompasskurs — posisjonen er ANSLÅTT',
    },
    {
      ...felles,
      sensorId: 'ovelse-s7-hentet',
      navn: 'ØVING UGS-7 Seismisk (HENTET INN)',
      art: 'seismisk',
      ...pkt(700),
      posisjonMetode: 'kartlagt',
      geometri: { form: 'rundstrålende', radiusM: 20 },
      batteri: { enhet: 'prosent', prosent: 4, kilde: 'målt' },
      lenke: { kvalitet: 'ingen-lenke' },
      skjultGrad: 'godt-skjult',
      aktiv: false,
      fjernetMs: naaMs - 5_400_000,
      klokkeKilde: 'intern-rtc',
      merknad: 'Hentet inn ved batteribytte — dekker ingenting nå',
    },
    {
      ...felles,
      sensorId: 'ovelse-s8-tapt',
      navn: 'ØVING UGS-8 Seismisk østre',
      art: 'seismisk',
      ...pkt(800),
      posisjonMetode: 'kartlagt',
      geometri: { form: 'rundstrålende', radiusM: 20 },
      batteri: { enhet: 'prosent', prosent: 41, kilde: 'anslått' },
      // Tapt lenke: den REGISTRERER kanskje fortsatt, men vi får ikke varsel.
      lenke: { kvalitet: 'tapt', sistHortMs: naaMs - 14_400_000, hopp: 4 },
      skjultGrad: 'godt-skjult',
      aktiv: true,
      klokkeKilde: 'intern-rtc',
    },
  ];
}

/**
 * Utløsningene. Tidene er relative til `naaMs` slik at alderstallene er
 * DETERMINISTISKE — ellers kan man ikke se om «pågår nå» faktisk sorteres først.
 */
function ovelseUtlosninger(naaMs: number): UgsUtlosning[] {
  return [
    // ── A. Ren sekvens: 0 m → 210 m → 390 m på 280 s. Gange. ────────────────
    {
      maling: 'lyd',
      utlosningId: 'ovelse-u-a1',
      sensorId: 'ovelse-s1-akustisk',
      tidspunktMs: naaMs - 300_000,
      tidsusikkerhetMs: 250,
      seq: 411,
      lydklasse: 'skritt',
      klassekonfidens: 0.61,
      bandFraHz: 40,
      bandTilHz: 800,
      nivaDbRe20uPa: 47.5,
      stoyGulvDbRe20uPa: 34,
      varighetMs: 4200,
    },
    {
      maling: 'terskel',
      utlosningId: 'ovelse-u-a2',
      sensorId: 'ovelse-s2-seismisk',
      tidspunktMs: naaMs - 150_000,
      // Fri RTC siden i går: 1,5 s slark er et tall vi kan forsvare, og det er
      // dette som gjør fartsintervallet bredt.
      tidsusikkerhetMs: 1500,
      seq: 88,
      amplitude: 0.31,
      amplitudeEnhet: 'mm-per-s',
      varighetMs: 2600,
      terskelverdi: 0.08,
      skrittrateHz: 1.7,
    },
    {
      maling: 'retning',
      utlosningId: 'ovelse-u-a3',
      sensorId: 'ovelse-s3-pir',
      tidspunktMs: naaMs - 20_000,
      tidsusikkerhetMs: 5,
      seq: 12,
      aksekursDeg: 70,
      aksekursUsikkerhetDeg: 8,
      passering: 'med-akse',
      straleAvstandM: 1.2,
      straleMellomtidMs: 780,
      antallPasseringer: 1,
    },

    // ── B. 11 m og 2 s: rekkefølgen kan IKKE bestemmes. ─────────────────────
    {
      maling: 'terskel',
      utlosningId: 'ovelse-u-b1',
      sensorId: 'ovelse-s6-magnetisk',
      tidspunktMs: naaMs - 10_800_000,
      // Klokkekilde «ukjent» ⇒ 4 s slark. Sammen med 2 s mellom utløsningene er
      // rekkefølgen prinsipielt ubestemt, og da finnes ingen retning å melde.
      tidsusikkerhetMs: 4000,
      amplitude: 38,
      amplitudeEnhet: 'nT',
      varighetMs: 900,
    },
    {
      maling: 'retning',
      utlosningId: 'ovelse-u-b2',
      sensorId: 'ovelse-s3-pir',
      tidspunktMs: naaMs - 10_798_000,
      tidsusikkerhetMs: 5,
      aksekursDeg: 70,
      aksekursUsikkerhetDeg: 8,
      passering: 'mot-akse',
      antallPasseringer: 2,
    },

    // ── C. 520 m på 20 s = 26 m/s. Umulig til fots. ─────────────────────────
    {
      maling: 'terskel',
      utlosningId: 'ovelse-u-c1',
      sensorId: 'ovelse-s5-trad',
      tidspunktMs: naaMs - 3_600_000,
      tidsusikkerhetMs: 20,
      amplitude: 1,
      amplitudeEnhet: 'binær',
      varighetMs: 0,
    },
    {
      maling: 'lyd',
      utlosningId: 'ovelse-u-c2',
      sensorId: 'ovelse-s1-akustisk',
      tidspunktMs: naaMs - 3_580_000,
      tidsusikkerhetMs: 250,
      lydklasse: 'kjøretøy-diesel',
      klassekonfidens: 0.74,
      bandFraHz: 20,
      bandTilHz: 400,
      nivaDbRe20uPa: 63.2,
      stoyGulvDbRe20uPa: 33,
      varighetMs: 21_000,
      dominantFrekvensHz: 92,
    },

    // ── D. Enslig kamerautløsning: ett punkt, ingen retning. ────────────────
    {
      maling: 'bilde',
      utlosningId: 'ovelse-u-d1',
      sensorId: 'ovelse-s4-kamera',
      tidspunktMs: naaMs - 900_000,
      tidsusikkerhetMs: 400,
      bildeId: 'ovelse:ugs4:0001',
      antallBilder: 3,
      utlosningsarsak: 'pir',
      vurdertAvMenneske: false,
      lysforhold: 'ir-blits',
    },

    // ── E. Ett bilde ingen har åpnet, og ett et menneske HAR sett. ──────────
    {
      maling: 'bilde',
      utlosningId: 'ovelse-u-e1',
      sensorId: 'ovelse-s4-kamera',
      tidspunktMs: naaMs - 4_000_000,
      tidsusikkerhetMs: 400,
      bildeId: 'ovelse:ugs4:0002',
      antallBilder: 1,
      utlosningsarsak: 'tidsintervall',
      vurdertAvMenneske: false,
      lysforhold: 'dagslys',
    },
    {
      maling: 'bilde',
      utlosningId: 'ovelse-u-e2',
      sensorId: 'ovelse-s4-kamera',
      tidspunktMs: naaMs - 7_200_000,
      tidsusikkerhetMs: 400,
      bildeId: 'ovelse:ugs4:0003',
      antallBilder: 2,
      utlosningsarsak: 'pir',
      // Den ENESTE UGS-verdien som fortjener høy konfidens: et menneske har sett
      // bildet og sagt hva det var.
      vurdertAvMenneske: true,
      menneskeligVurdering: 'dyr',
      vurdertAv: 'ØVING VARG 3 / lagfører',
      lysforhold: 'dagslys',
    },
  ];
}

/**
 * Ruta dekningsanalysen kjøres langs: 900 m av samme skogsvei.
 *
 * Den er lagt slik at svaret er interessant: største hull er strekket der den
 * HENTEDE noden sto, og strekket rundt den med TAPT lenke skifter fra «dekket»
 * til «hull» i det man krever varsling. Det er hele grunnen til at
 * `krevSambandslenke` er en bryter og ikke en antakelse.
 */
function ovelseRuter(): Rute[] {
  return [
    {
      id: 'ovelse-rute-sti',
      navn: 'ØVING Sti langs skogsvei — vest mot øst',
      kilde: 'gått-med-gps',
      punkt: [pkt(0), pkt(300), pkt(620), pkt(900)],
      merknad: 'Gått med GPS ved utlegging. 900 m fra vestre ende til bekken.',
    },
    {
      id: 'ovelse-rute-flanke',
      navn: 'ØVING Flankeakse nord — ANTATT',
      kilde: 'antatt',
      punkt: [pkt(120, -180), pkt(420, -160), pkt(700, -190)],
      merknad: 'Ingen har gått denne. Tatt fra kart fordi terrenget ser framkommelig ut.',
    },
  ];
}

/**
 * Bygg hele øvingsbildet, i den rekkefølgen broen ville sendt det.
 *
 * MERK at ingenting her setter `ovelse: true` for hånd. Øvingskilden stempler
 * hver melding på vei ut (`lagOvelseskilde`), og `lib/lager.ts` bærer stempelet
 * ned på sensorraden, hendelsen og ruta. To steder å sette det er ett sted å
 * glemme det.
 */
export function ovelsemeldinger(naaMs: number): UllMelding[] {
  return [
    { type: 'lag', lag: OVELSE_LAG },
    ...ovelseRuter().map((rute): UllMelding => ({ type: 'rute', rute })),
    ...ovelseSensorer(naaMs).map((sensor): UllMelding => ({ type: 'sensor', sensor })),
    ...ovelseUtlosninger(naaMs).map((utlosning): UllMelding => ({ type: 'utlosning', utlosning })),
  ];
}

/**
 * PULSEN — det som kommer mens øvingen går.
 *
 * ══ HVORFOR DEN ER SÅ SKJEV, OG HVA DEN BEVISST IKKE GJØR ══════════════════
 * Den viktigste domeneregelen i ULL er at LIVSTEGN og SISTE UTLØSNING er to
 * forskjellige aldre (se `sensorStatuser` i `lib/lager.ts`). En geofon som ikke
 * har utløst på en time er helt frisk — det har bare ikke gått noen der. Pulsen
 * er bygget for å kunne øve på nettopp det skillet:
 *
 *   * `status` HVER puls på de tre nodene med god lenke. Livstegnet holder seg
 *     ferskt, mens «siste utløsning» blir eldre og eldre. Det er den ene
 *     sammenstillingen jegeren må kunne lese uten å tenke.
 *   * BATTERIET SYNKER, sakte og ujevnt. Kamera-noden starter på 14 % og går
 *     under 10: batteriadvarselen skal DUKKE OPP mens man ser på, og forbeholdet
 *     om at prosent kommer fra en flat utladningskurve skal følge med.
 *   * NODEN MED TAPT LENKE (`ovelse-s8-tapt`) og den HENTEDE (`ovelse-s7-hentet`)
 *     melder ALDRI. De skal stå og bli eldre, fordi «ingen livstegn på fire
 *     timer» er signalet om at en node kan være funnet og fjernet.
 *   * ÉN NY UTLØSNING, sjelden (hver sjette puls), på den akustiske. Én utløsning
 *     ALENE bærer ingen retning — kontrakten nekter å analysere den, og det er
 *     riktig. Pulsen lager altså IKKE en pen ny sekvens; den lager et enslig
 *     punkt, som er det vanligste en UGS-post ser.
 *
 * Ingenting i pulsen gir en node en bedre klokke, en sikrere posisjon eller en
 * ferdig vurdering av et bilde. En jeger som øver på perfekt data lærer feil.
 */
export function ovelsepuls(naaMs: number, tikk: number): UllMelding[] {
  const ut: UllMelding[] = [];

  // Livstegn fra nodene som HAR lenke. Merk at dette er `status`, ikke
  // `utlosning` — det er hjertebank, ikke aktivitet.
  for (const id of ['ovelse-s1-akustisk', 'ovelse-s2-seismisk', 'ovelse-s3-pir']) {
    ut.push({
      type: 'status',
      sensorId: id,
      tsMs: naaMs,
      lenke: { kvalitet: 'god', sistHortMs: naaMs, hopp: id === 'ovelse-s2-seismisk' ? 2 : 1 },
    });
  }

  // Kameraet: marginal lenke, og batteriet synker under advarselsgrensa mens man
  // ser på. `kilde: 'målt'` beholdes — det er en ekte måling av en dårlig kurve,
  // ikke et anslag, og forskjellen står i advarselsteksten.
  ut.push({
    type: 'status',
    sensorId: 'ovelse-s4-kamera',
    tsMs: naaMs,
    batteri: { enhet: 'prosent', prosent: Math.max(4, 14 - Math.floor(tikk / 3)), kilde: 'målt' },
    lenke: { kvalitet: 'marginal', sistHortMs: naaMs, rssiDbm: -108 + (tikk % 4), snrDb: 2, hopp: 3 },
  });

  // Sjelden, ENSLIG utløsning. `tidsusikkerhetMs: 250` er den akustiske nodens
  // ekte slark — pulsen later ikke som klokka er bedre enn den er.
  if (tikk % 6 === 0) {
    ut.push({
      type: 'utlosning',
      utlosning: {
        maling: 'lyd',
        utlosningId: `ovelse-u-puls-${tikk}`,
        sensorId: 'ovelse-s1-akustisk',
        tidspunktMs: naaMs,
        tidsusikkerhetMs: 250,
        lydklasse: 'ukjent',
        klassekonfidens: 0.28,
        bandFraHz: 40,
        bandTilHz: 900,
        nivaDbRe20uPa: 41.2,
        stoyGulvDbRe20uPa: 34,
        varighetMs: 1800,
      },
    });
  }

  return ut;
}

/**
 * Lagets øvingsposisjon — kartet trenger et startpunkt.
 *
 * MERK at dette er et punkt PÅ AKSEN og ikke en utløsning: å utlede kartsenteret
 * av en oppdiktet utløsning ville vært å bruke en oppdiktet posisjon til noe.
 */
export const OVELSE_SENTER: LatLon = pkt(400);
